import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Space, Tag } from "antd";
import { Plus, Eye } from "lucide-react";
import dayjs from "dayjs";
import { useScaffold, useScaffoldInspections, useScaffoldCreate, useScaffoldUpdate, useScaffoldDelete, useScaffoldInspCreate, useScaffoldInspDelete, useCCClients, useCCUsers } from "../../../../api-hooks/compliance";

const STATUS_COLOR: Record<string, string> = { erected: "blue", handed_over: "green", modified: "orange", dismantled: "default" };

export default function ScaffoldTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [inspForm] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [inspModal, setInspModal] = useState<{ open: boolean; scaffoldId: number | null; rec: any }>({ open: false, scaffoldId: null, rec: null });
  const [viewInspModal, setViewInspModal] = useState<{ open: boolean; scaffoldId: number | null }>({ open: false, scaffoldId: null });
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useScaffold(cid, { page, limit: 20 });
  const { data: clients = [] } = useCCClients(cid);
  const { data: users = [] } = useCCUsers(cid);
  const { data: inspections = [] } = useScaffoldInspections(cid, viewInspModal.scaffoldId ?? 0);

  const rows = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string, closeFn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); closeFn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useScaffoldCreate(cid, cb("Scaffold registered.", () => setModal({ open: false, rec: null })));
  const update = useScaffoldUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const remove = useScaffoldDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const inspCreate = useScaffoldInspCreate(cid, cb("Inspection added.", () => setInspModal({ open: false, scaffoldId: null, rec: null })));
  const inspDelete = useScaffoldInspDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, erected_date: r.erected_date ? dayjs(r.erected_date) : null, handover_date: r.handover_date ? dayjs(r.handover_date) : null, dismantled_date: r.dismantled_date ? dayjs(r.dismantled_date) : null } : { status: "erected" });
  }

  const columns = [
    { title: "Ref #", dataIndex: "ref_number", key: "r" },
    { title: "Client", dataIndex: "client_name", key: "c", render: (v: string) => v?.trim() || "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "Erected", dataIndex: "erected_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Next Insp.", dataIndex: "next_inspection_date", key: "ni", render: (v: string) => {
      if (!v) return "—";
      const overdue = dayjs(v).isBefore(dayjs());
      return <Tag color={overdue ? "red" : "green"}>{dayjs(v).format("DD/MM/YY")}{overdue ? " OVERDUE" : ""}</Tag>;
    }},
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" icon={<Eye size={12} />} onClick={() => setViewInspModal({ open: true, scaffoldId: r.id })}>Inspections</Button>
        <Button size="small" onClick={() => setInspModal({ open: true, scaffoldId: r.id, rec: null })}>+ Insp</Button>
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const inspCols = [
    { title: "Date", dataIndex: "inspection_date", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Outcome", dataIndex: "outcome", key: "o", render: (v: string) => <Tag color={{ pass: "green", advisory: "orange", fail: "red" }[v]}>{v?.toUpperCase()}</Tag> },
    { title: "Next Due", dataIndex: "next_due", key: "nd", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Findings", dataIndex: "findings", key: "f", render: (v: string) => v || "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => inspDelete.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">Scaffold Registers</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>Register Scaffold</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />

      {/* Scaffold Modal */}
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? `Edit ${modal.rec.ref_number}` : "Register Scaffold"} width={520}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, erected_date: vals.erected_date?.format("YYYY-MM-DD"), handover_date: vals.handover_date?.format("YYYY-MM-DD"), dismantled_date: vals.dismantled_date?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="client_id" label="Client" rules={[{ required: true }]}><Select showSearch options={(clients as any[]).map(c => ({ value: c.id, label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.business_name || c.email }))} /></Form.Item>
            <Form.Item name="ref_number" label="Reference #" rules={[{ required: true }]}><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="erected_date" label="Erected Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="status" label="Status"><Select options={["erected","handed_over","modified","dismantled"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="handover_date" label="Handover Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="dismantled_date" label="Dismantled Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="load_class" label="Load Class"><Input placeholder="e.g. Class 3" /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Add Inspection Modal */}
      <Modal open={inspModal.open} onCancel={() => setInspModal({ open: false, scaffoldId: null, rec: null })} footer={null} destroyOnClose title="Add Scaffold Inspection" width={420}>
        <Form form={inspForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          inspCreate.mutate({ ...vals, scaffold_id: inspModal.scaffoldId, inspection_date: vals.inspection_date?.format("YYYY-MM-DD"), next_due: vals.next_due?.format("YYYY-MM-DD") });
        }}>
          <Form.Item name="inspected_by" label="Inspected By (User ID)" rules={[{ required: true }]}><Input type="number" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="inspection_date" label="Inspection Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="outcome" label="Outcome" rules={[{ required: true }]}><Select options={["pass","advisory","fail"].map(v => ({ value: v, label: v.toUpperCase() }))} /></Form.Item>
          </div>
          <Form.Item name="next_due" label="Next Inspection Due"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          <Form.Item name="findings" label="Findings"><Input.TextArea rows={3} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={inspCreate.isPending}>Save</Button>
            <Button onClick={() => setInspModal({ open: false, scaffoldId: null, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* View Inspections Modal */}
      <Modal open={viewInspModal.open} onCancel={() => setViewInspModal({ open: false, scaffoldId: null })} footer={null} title={`Inspections — Scaffold #${viewInspModal.scaffoldId}`} width={600}>
        <Table rowKey="id" dataSource={inspections as any[]} columns={inspCols} size="small" pagination={false} />
      </Modal>
    </div>
  );
}
